import React from 'react';

const BAR_COUNT = 24;
// Небольшая псевдослучайная развёртка длительности/задержки на столбик —
// чтобы бегущая волна не выглядела механически-синхронной (как в артборде).
const BAR_DURATION = (i) => 0.6 + (i % 5) * 0.1;
const BAR_DELAY = (i) => i * 0.02;
// RMS живой речи в PCM обычно небольшой (~0.05–0.2) — усиливаем для заметной волны.
const LEVEL_GAIN = 4;

function StatRow({ label, value }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <b className="font-mono font-medium text-paper-ink">{value}</b>
    </div>
  );
}

/**
 * Правая колонка живой записи по «Бумаге» (артборд «Распознавание», состояние
 * LIVE·on): карточка состояния с волной и устройством, карточка счётчиков,
 * пауза и остановка.
 *
 * Волна чёрная, а не акцентная: в артборде розовым помечено только состояние
 * («ЗАПИСЬ · 04:12» с точкой), а сам уровень сигнала — чёрные столбики.
 * Высота столбика идёт от `level` — RMS текущего PCM-кадра из
 * useStreamingCapture.
 */
function Wave({ level, paused }) {
  return (
    <div className="flex h-10 items-end gap-[3px]" role="img"
         aria-label="Уровень сигнала микрофона">
      {Array.from({ length: BAR_COUNT }, (_, i) => {
        const amplitude = Math.max(0.1, Math.min(1, level * LEVEL_GAIN * (0.6 + (i % 5) * 0.15)));
        return (
          <div
            key={i}
            className="w-[3px] rounded-[2px] bg-paper-ink animate-wave"
            style={{
              height: `${Math.round(amplitude * 40)}px`,
              animationDuration: `${BAR_DURATION(i)}s`,
              animationDelay: `${BAR_DELAY(i)}s`,
              animationPlayState: paused ? 'paused' : 'running',
            }}
          />
        );
      })}
    </div>
  );
}

/** «● ЗАПИСЬ · 04:12» — розовым, как в артборде. */
export function RecordingBadge({ paused, duration, label }) {
  return (
    <span className="flex items-center gap-2 font-mono text-[12px] font-bold uppercase tracking-[0.1em] text-paper-accent">
      <span className="w-2 h-2 rounded-full bg-paper-accent animate-pulse" aria-hidden="true" />
      {label || (paused ? 'Пауза' : 'Запись')} · {duration ?? '00:00'}
    </span>
  );
}

/**
 * Низ экрана живой записи на телефоне (мобильный макет «Бумаги», экран
 * «Запись»): волна по центру и две липкие кнопки. Счётчики слов и темпа на
 * телефоне не показываем — место отдано тексту; «спишется» — в строке сверху.
 */
// stopLabel/badge — для досылки после «Стопа» (фаза finishing): микрофон уже
// выключен, кнопка останавливает сразу, без досылки.
const STOP_LABEL = 'Остановить и сохранить';

export function MicBar({ level = 0, paused = false, onTogglePause, onStop, stopDisabled = false, stopLabel = STOP_LABEL }) {
  return (
    <div className="shrink-0 flex flex-col items-center gap-3 font-ui">
      <Wave level={level} paused={paused} />
      <div className="w-full flex gap-2">
        {onTogglePause && (
          <button type="button" onClick={onTogglePause}
            className="w-[36%] h-[52px] rounded-[14px] bg-paper-card text-[15px] font-medium text-paper-ink">
            {paused ? 'Продолжить' : 'Пауза'}
          </button>
        )}
        <button type="button" onClick={onStop} disabled={stopDisabled}
          className="flex-1 h-[52px] rounded-[14px] bg-paper-ink text-[15px] font-semibold text-white disabled:bg-paper-bg disabled:text-paper-hint">
          {stopLabel}
        </button>
      </div>
    </div>
  );
}

export default function MicPanel({
  level = 0, deviceLabel, stats = {}, onStop, stopDisabled = false,
  paused = false, onTogglePause, stopLabel = STOP_LABEL, badge,
}) {
  return (
    <aside className="flex flex-col gap-3 font-ui">
      <div className="bg-paper-card rounded-panel p-5">
        <RecordingBadge paused={paused} duration={stats.duration} label={badge} />

        <div className="mt-4">
          <Wave level={level} paused={paused} />
        </div>

        <div className="mt-[10px] truncate font-mono text-[12px] text-paper-ink-3">
          {deviceLabel || 'Микрофон'}
        </div>
      </div>

      <div className="bg-paper-card rounded-panel p-5 flex flex-col gap-[6px] text-[13px] text-paper-ink-2">
        <StatRow label="Слов" value={stats.words ?? 0} />
        <StatRow label="Темп" value={stats.pace ?? '—'} />
        {/* «Спишется» в артборде стоит третьей строкой. Показываем ровно то, что
            знаем: минуты записи. Округляем ВВЕРХ — так же считает квота. */}
        {stats.spend && <StatRow label="Спишется" value={stats.spend} />}
      </div>

      {onTogglePause && (
        <button type="button" onClick={onTogglePause}
          className="rounded-control bg-paper-card py-3 text-[14px] font-medium text-paper-ink hover:bg-paper-panel transition-colors">
          {paused ? 'Продолжить' : 'Пауза'}
        </button>
      )}

      <button type="button" onClick={onStop} disabled={stopDisabled}
        className="rounded-control bg-paper-ink py-[14px] text-[14px] font-semibold text-white disabled:bg-paper-bg disabled:text-paper-hint transition-colors">
        {stopLabel}
      </button>
    </aside>
  );
}
